/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { FC, ReactNode } from "react";
import iconError from "assets/error_icon.svg";
import iconInfo from "assets/icon_info.svg";
import { useTranslation } from "react-i18next";
import { DialogButton } from "./DialogButton";

export interface ConfirmDialogProps {
  open: boolean;
  title?: string | ReactNode;
  message: string | ReactNode;
  negativeButtonText?: string;
  positiveButtonText: string;
  caution?: boolean;
  onNegativeButtonClick?: () => void;
  onPositiveButtonClick: () => void;
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  negativeButtonText,
  positiveButtonText,
  caution = false,
  onNegativeButtonClick,
  onPositiveButtonClick,
}) => {
  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "30vw",
          minHeight: "40vh",
        },
      }}
    >
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent
        sx={{
          "&.MuiDialogContent-root": {
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.2em",
          },
        }}
      >
        {message}
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} gap={2}>
          {onNegativeButtonClick && (
            <DialogButton onClick={onNegativeButtonClick}>
              {negativeButtonText}
            </DialogButton>
          )}
          <DialogButton
            color={caution ? "error" : undefined}
            onClick={onPositiveButtonClick}
          >
            {positiveButtonText}
          </DialogButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

type TitleType = "Information" | "Alert";
interface TitleDef {
  icon: string;
  titleCode: string;
}
const iconMap: { [position: string]: TitleDef } = {
  Information: {
    icon: iconInfo,
    titleCode: "AWVUI-MSG137",
  },
  Alert: {
    icon: iconError,
    titleCode: "AWVUI-MSG136",
  },
};
interface ConfirmDialogTitleProps {
  type: TitleType;
}
export const ConfirmDialogTitle: FC<ConfirmDialogTitleProps> = ({
  type,
}: ConfirmDialogTitleProps) => {
  const { t } = useTranslation();
  return (
    <Stack direction={"row"} gap={1}>
      <img src={iconMap[type].icon} alt="icon" width={32} height={32} />
      <Typography variant="h5">{t(iconMap[type].titleCode)}</Typography>
    </Stack>
  );
};
